import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { POST_CASTING } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2, Sparkles, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_TYPES = ['Singer', 'Dancer', 'Musician', 'Actor', 'DJ', 'Model', 'Comedian', 'Choreographer', 'Voice Artist', 'Photographer', 'Influencer', 'Content Creator'];

const PostCasting = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', role_type: '', location: '',
    deadline: '', budget: '', requirements: '',
  });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/castings', form);
      toast.success('Casting call posted!', { description: 'Artists can now apply.' });
      navigate(`/castings/${data.casting.casting_id}`);
    } catch (err) {
      toast.error('Could not post', { description: err?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-24 max-w-3xl mx-auto px-6 sm:px-12">
      <div className="mb-10 text-center">
        <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs uppercase tracking-widest text-emerald-300">
          <Briefcase className="h-3.5 w-3.5" /> For casting directors
        </div>
        <h1 className="font-display text-4xl font-bold text-white tracking-tight mb-3">
          Post a <span className="rainbow-text">casting call</span>
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto">Reach 12,000+ verified artists across India. Applications land in your dashboard.</p>
      </div>

      <form onSubmit={submit} className="glass-strong rounded-3xl border border-white/10 p-8 space-y-5">
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Title *</Label>
          <Input required value={form.title} onChange={update('title')} placeholder="Lead Female Singer — Brand Anthem 2026" data-testid={POST_CASTING.titleInput} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Description *</Label>
          <Textarea required rows={5} value={form.description} onChange={update('description')} placeholder="Project context, what artists should expect…" data-testid={POST_CASTING.descriptionInput} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Role type *</Label>
            <Select value={form.role_type} onValueChange={(v) => setForm((f) => ({ ...f, role_type: v }))}>
              <SelectTrigger data-testid={POST_CASTING.roleTypeInput} className="bg-white/5 border-white/10 mt-1"><SelectValue placeholder="Select role…" /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 text-slate-200 max-h-[300px]">
                {ROLE_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Location *</Label>
            <Input required value={form.location} onChange={update('location')} placeholder="Mumbai, Bengaluru…" data-testid={POST_CASTING.locationInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Deadline *</Label>
            <Input type="date" required value={form.deadline} onChange={update('deadline')} data-testid={POST_CASTING.deadlineInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Budget</Label>
            <Input value={form.budget} onChange={update('budget')} placeholder="₹4-7L" data-testid={POST_CASTING.budgetInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Requirements</Label>
          <Textarea rows={3} value={form.requirements} onChange={update('requirements')} placeholder="Age, gender, languages, experience…" data-testid={POST_CASTING.requirementsInput} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <Button type="submit" disabled={loading} data-testid={POST_CASTING.submitButton} className="w-full btn-rainbow text-white border-0 rounded-xl py-6 text-base">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Publishing…</> : <><Sparkles className="h-4 w-4 mr-2" />Publish casting call</>}
        </Button>
      </form>
    </div>
  );
};

export default PostCasting;
